import React from 'react';
import kebabCase from 'lodash/kebabCase';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';

import messages from '../../messages';
import { ViewAs } from '../../types';
import { isViewSettingsPanelAndTailoredViewExperimentEnabled } from '../../utils';

type Props = {
	title: string;
	isChecked: boolean;
	isDisabled?: boolean;
	onChange: (event: React.ChangeEvent<HTMLInputElement>, analyticsEvent: UIAnalyticsEvent) => void;
	keyboardShortcut?: string;
	subtitleLozenge?: string;
	testId?: string;
	viewAs?: (typeof ViewAs)[keyof typeof ViewAs];
};

export const ViewSettingsToggle = ({
	title,
	isChecked,
	isDisabled,
	onChange,
	keyboardShortcut,
	subtitleLozenge,
	testId,
	viewAs = ViewAs.POPUP,
}: Props) => {
	const id = kebabCase(`jira-software-view-settings-${title}`);
	const descriptionId = `${id}-description`;
	const { formatMessage } = useIntl();
	const [linkIssuesExp] = UNSAFE_noExposureExp('jsw_link_issues_from_board_cards');
	return (
		<Box
			xcss={
				viewAs === ViewAs.PANEL && isViewSettingsPanelAndTailoredViewExperimentEnabled()
					? panelContainerStyles
					: popupContainerStyles
			}
		>
			<Inline space="space.200" spread="space-between" alignBlock="center">
				{
					// LD feature flag wrapper required to release to Release Track enrolled customers
					// eslint-disable-next-line jira/ff/no-preconditioning
					ff('jsw.perf.issue-links-on-board-cards') &&
					// Check if Milestone 4 for issue links details popup is enabled
					fg('jsw_enable_issue_links_stats_on_view_settings_m4') &&
					// check if the experiment is enabled
					linkIssuesExp.get('enableIssueLinkExperiment', false) ? (
						<Inline space="space.100" alignBlock="center">
							<label data-testid={testId} htmlFor={id}>
								{title}
							</label>
							{subtitleLozenge && <Lozenge appearance="new">{subtitleLozenge}</Lozenge>}
						</Inline>
					) : (
						<label data-testid={testId} htmlFor={id}>
							{title}
						</label>
					)
				}
				<Box xcss={tooltipContainerStyles}>
					{keyboardShortcut && <Lozenge>{keyboardShortcut}</Lozenge>}
					<Toggle
						id={id}
						isChecked={isChecked}
						onChange={onChange}
						isDisabled={isDisabled}
						{...(keyboardShortcut && { descriptionId })}
					/>
					{keyboardShortcut && (
						<ScreenReaderText id={descriptionId}>
							{formatMessage(messages.toggleHint, {
								key: keyboardShortcut,
								panel: title,
							})}
						</ScreenReaderText>
					)}
				</Box>
			</Inline>
		</Box>
	);
};

const popupContainerStyles = xcss({
	margin: `${token('space.100', '8px')} ${token('space.200', '16px')} ${token(
		'space.100',
		'8px',
	)} ${token('space.200', '16px')}`,
	minWidth: '200px',
});

const panelContainerStyles = xcss({
	margin: `${token('space.100', '4px')} auto`,
	minWidth: '200px',
});

const tooltipContainerStyles = xcss({
	color: 'color.text.subtlest',
	whiteSpace: 'nowrap',
	lineHeight: 0,
});
